import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { QueryEmptyState } from '../components/dashboard/QueryEmptyState';
import { QueryErrorState } from '../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../components/dashboard/QueryLoadingBlock';
import { StatCard } from '../components/dashboard/StatCard';
import { StatsGrid } from '../components/dashboard/StatsGrid';
import { apiErrorMessage } from '../i18n';
import { api } from '../lib/api';
import { formatCount } from '../lib/dashboard/formatCount';
import { useQueryRetry } from '../lib/dashboard/useQueryRetry';
import { setSelectedOrganisationId, useOrganisationId } from '../lib/session';
import { AdminCreateOrganisationCard } from './admin/AdminCreateOrganisationCard';
import { AdminMembersCard } from './admin/AdminMembersCard';
import { AdminOrganisationCard } from './admin/AdminOrganisationCard';
import { AdminSwitchOrganisationCard } from './admin/AdminSwitchOrganisationCard';

/**
 * Tenant admin — organisation identity, members, switcher, and create-org.
 * Layout mirrors the two-column Admin reference (stats → details | switcher).
 */
export function AdminPage() {
  const { t } = useTranslation('admin');
  const organisationId = useOrganisationId();
  const queryClient = useQueryClient();
  const [renameError, setRenameError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  const orgsQuery = useQuery({
    queryKey: ['my-organisations'],
    queryFn: api.listMyOrganisations,
  });
  const membersQuery = useQuery({
    queryKey: ['org-members', organisationId],
    queryFn: () => api.listOrganisationMembers(organisationId),
  });
  const invitesQuery = useQuery({
    queryKey: ['org-invites', organisationId],
    queryFn: () => api.listInvites(organisationId),
  });
  const screensQuery = useQuery({
    queryKey: ['screens', organisationId],
    queryFn: () => api.listScreens(organisationId),
  });
  const locationsQuery = useQuery({
    queryKey: ['locations', organisationId],
    queryFn: () => api.listLocations(organisationId),
  });

  const current = orgsQuery.data?.find((org) => org.id === organisationId);
  const orgsRetry = useQueryRetry(() => orgsQuery.refetch());
  const membersRetry = useQueryRetry(() => membersQuery.refetch());

  const ownerCount =
    membersQuery.data?.filter((member) => member.role === 'organisation_owner').length ?? 0;

  const switchOrganisation = (nextId: string) => {
    if (nextId === organisationId) return;
    setSelectedOrganisationId(nextId);
    void queryClient.invalidateQueries();
  };

  const renameOrg = useMutation({
    mutationFn: (name: string) => api.updateOrganisation(organisationId, { name }),
    onSuccess: async () => {
      setRenameError(null);
      await queryClient.invalidateQueries({ queryKey: ['my-organisations'] });
    },
    onError: (err) => {
      setRenameError(apiErrorMessage(err, t('page.renameError')));
    },
  });

  // Clear inline rename error when the active tenant changes.
  useEffect(() => {
    setRenameError(null);
  }, [organisationId]);

  const createOrg = useMutation({
    mutationFn: (input: { name: string; slug: string }) => api.createOrganisation(input),
    onSuccess: async (org) => {
      setCreateError(null);
      await queryClient.invalidateQueries({ queryKey: ['my-organisations'] });
      switchOrganisation(org.id);
    },
    onError: (err) => {
      setCreateError(apiErrorMessage(err, t('page.createError')));
    },
  });

  return (
    <div className="space-y-6">
      <StatsGrid>
        <StatCard
          label={t('page.stats.members')}
          value={formatCount(membersQuery.data?.length, membersQuery.isPending)}
          note={membersQuery.isPending ? undefined : t('ownerCount', { count: ownerCount })}
        />
        <StatCard
          label={t('page.stats.organisations')}
          value={formatCount(orgsQuery.data?.length, orgsQuery.isPending)}
          note={current ? t('page.stats.activeNote', { name: current.name }) : undefined}
        />
        <StatCard
          label={t('page.stats.displays')}
          value={formatCount(screensQuery.data?.length, screensQuery.isPending)}
          note={
            locationsQuery.isPending
              ? undefined
              : t('locationCount', { count: locationsQuery.data?.length ?? 0 })
          }
        />
        <StatCard
          label={t('page.stats.pendingInvites')}
          value={formatCount(invitesQuery.data?.length, invitesQuery.isPending)}
          note={
            invitesQuery.isPending || (invitesQuery.data?.length ?? 0) === 0
              ? undefined
              : t('page.stats.manageInTeams')
          }
        />
      </StatsGrid>

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="flex flex-col gap-6 lg:col-span-3">
          {orgsQuery.isError || orgsRetry.isRetrying ? (
            <section
              className="rounded-[14px] border border-line px-5 py-5"
              style={{ background: 'var(--gs-panel)' }}
            >
              <QueryErrorState
                message={t('page.loadError')}
                onRetry={orgsRetry.retry}
                isRetrying={orgsRetry.isRetrying}
              />
            </section>
          ) : orgsQuery.isPending ? (
            <section
              className="rounded-[14px] border border-line px-5 py-5"
              style={{ background: 'var(--gs-panel)' }}
            >
              <QueryLoadingBlock />
            </section>
          ) : !current ? (
            <section
              className="rounded-[14px] border border-line px-5 py-5"
              style={{ background: 'var(--gs-panel)' }}
            >
              <QueryEmptyState>{t('page.missingOrganisation')}</QueryEmptyState>
            </section>
          ) : (
            <AdminOrganisationCard
              membership={current}
              renaming={renameOrg.isPending}
              renameError={renameError}
              onRename={(name) => renameOrg.mutate(name)}
            />
          )}

          <AdminMembersCard
            members={membersQuery.data}
            isPending={membersQuery.isPending}
            isError={membersQuery.isError}
            isRetrying={membersRetry.isRetrying}
            onRetry={membersRetry.retry}
          />
        </div>

        <div className="flex flex-col gap-6 lg:col-span-2">
          <AdminSwitchOrganisationCard
            memberships={orgsQuery.data}
            activeId={organisationId}
            isPending={orgsQuery.isPending}
            onSwitch={switchOrganisation}
          />
          <AdminCreateOrganisationCard
            creating={createOrg.isPending}
            error={createError}
            onCreate={(input) => {
              setCreateError(null);
              createOrg.mutate(input);
            }}
          />
        </div>
      </div>
    </div>
  );
}
